import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { setDarkActive } from '../theme/darkPalette';

export type ThemeMode = 'light' | 'dark';

const STORAGE_KEY = 'asg_dashboard_theme_v1';

interface ThemeContextType {
  mode: ThemeMode;
  /** Preferencia del usuario, sin tener en cuenta el ámbito donde se aplica. */
  prefersDark: boolean;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/**
 * El modo oscuro solo se aplica dentro del dashboard; el resto de la app
 * (web pública y app de comensales) se mantiene siempre en claro.
 */
const ThemeScopeContext = createContext(false);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>('dark');

  // Se sincroniza en render para que `tc()` ya vea el valor nuevo en este ciclo.
  setDarkActive(mode === 'dark');

  useEffect(() => {
    let cancelled = false;
    AsyncStorage.getItem(STORAGE_KEY)
      .then((stored) => {
        if (cancelled) return;
        if (stored === 'dark' || stored === 'light') setModeState(stored);
      })
      .catch((e) => {
        console.warn('[theme] No se pudo leer la preferencia de tema', e);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    AsyncStorage.setItem(STORAGE_KEY, next).catch((e) => {
      console.warn('[theme] No se pudo guardar la preferencia de tema', e);
    });
  }, []);

  const toggleMode = useCallback(() => {
    setModeState((prev) => {
      const next: ThemeMode = prev === 'dark' ? 'light' : 'dark';
      AsyncStorage.setItem(STORAGE_KEY, next).catch((e) => {
        console.warn('[theme] No se pudo guardar la preferencia de tema', e);
      });
      return next;
    });
  }, []);

  const value = useMemo<ThemeContextType>(
    () => ({ mode, prefersDark: mode === 'dark', setMode, toggleMode }),
    [mode, setMode, toggleMode]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/** Activa el modo oscuro para el subárbol (lo usa el dashboard). */
export function ThemeScope({ children }: { children: ReactNode }) {
  return <ThemeScopeContext.Provider value={true}>{children}</ThemeScopeContext.Provider>;
}

export function useTheme(): ThemeContextType {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    return {
      mode: 'dark',
      prefersDark: true,
      setMode: () => {},
      toggleMode: () => {},
    };
  }
  return ctx;
}

/** `true` solo si el usuario eligió oscuro y estamos dentro del dashboard. */
export function useIsDark(): boolean {
  const ctx = useContext(ThemeContext);
  const inScope = useContext(ThemeScopeContext);
  return inScope && ctx?.mode === 'dark';
}
